/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/login.scss';

import React, {FocusEventHandler, KeyboardEventHandler, MouseEventHandler, useRef, useState} from 'react';
import {ChangeKeyFunction, Link, Loader, preventDefault} from '@dvrd/dvr-controls';
import {CredentialResponse, GoogleLogin} from '@react-oauth/google';
import {LoginErrors} from "../../../util/interfaces";
import {BolmateButton, BolmateInput, BolmatePasswordInput} from "../../controls";
import Image from "../../misc/image";
import {FORGOT_PASSWORD_ROUTE, REGISTER_ROUTE} from "../../../util/constants";

interface Props {
    onSubmit: MouseEventHandler & KeyboardEventHandler;
    onChange: ChangeKeyFunction<string>;
    onFocusField: (key: keyof LoginErrors) => FocusEventHandler;
    onClickRegister: MouseEventHandler;
    onGoogleLogin: (response: CredentialResponse) => void;
    onGoogleError: () => void;
    onClickGoogle: MouseEventHandler;
    onClickReset: MouseEventHandler;
    email: string;
    password: string;
    loading: boolean;
    errors: LoginErrors;
}

export default function Login(props: Props) {
    const {
        onGoogleLogin, loading, onClickGoogle, onClickRegister, onClickReset, errors, onChange, onFocusField,
        onSubmit, password, email, onGoogleError
    } = props;
    const [headerHeight, setHeaderHeight] = useState(0);
    const headerRef = useRef<HTMLDivElement>(null);

    function renderGoogle() {
        return (
            <div onClick={onClickGoogle} className='google-button-wrapper'>
                <GoogleLogin onSuccess={onGoogleLogin} onError={onGoogleError} context='signin' text='signin_with'
                             width='400px'/>
            </div>
        );
    }

    function _setHeaderHeight() {
        if (!headerRef.current) return;
        setHeaderHeight(headerRef.current.getBoundingClientRect().height);
    }

    return (
        <div className='login'>
            <div className='logo-header' ref={headerRef}>
                <Image name='logo' className='logo' onLoad={_setHeaderHeight}/>
                {/*<img className='logo' src={getImg('logo')} alt='Bolmate' onLoad={_setHeaderHeight}/>*/}
            </div>
            <div style={{height: `${headerHeight}px`}}/>
            <div>
                <div className='header-container'>
                    <h1 className='header-title'>Inloggen in je account</h1>
                    <label className='sub-title'>Welkom bij Bolmate</label>
                </div>
                <form onSubmit={preventDefault} className='form'>
                    <Loader active={loading}/>
                    <BolmateInput value={email} onChange={onChange('email')} error={errors.email}
                                  onFocus={onFocusField('email')} label='E-mailadres *'
                                  inputProps={{placeholder: 'E-mailadres', autoComplete: 'username'}} onEnter={onSubmit}
                                  autoFocus/>
                    <BolmatePasswordInput value={password} onChange={onChange('password')} error={errors.password}
                                          onFocus={onFocusField('password')} label='Wachtwoord *'
                                          inputProps={{placeholder: 'Wachtwoord', autoComplete: 'current-password'}}
                                          onEnter={onSubmit}/>
                    {!!errors.general && <div className='general-error'>
                        <p>Ongeldige combinatie van e-mailadres en wachtwoord.
                            <br/><br/>Weet je zeker dat dit wel het juiste e-mailadres is?<br/>90% van de tijd vergissen
                            gebruikers zich in het e-mailadres.</p>
                    </div>}
                    <a className='reset-link' href={FORGOT_PASSWORD_ROUTE} onClick={onClickReset}>Wachtwoord
                        vergeten?</a>
                </form>
                <div className='btns-container'>
                    <BolmateButton onClick={onSubmit} label='Log in' className='submit-btn' disabled={loading}/>
                    {renderGoogle()}
                </div>
                <label className='register-label'>Heb je nog geen account? <Link route={REGISTER_ROUTE}
                                                                                 onClick={onClickRegister}
                                                                                 underline>Registreren</Link></label>
            </div>
        </div>
    )
}